import React, { useEffect, useState, useCallback } from "react";
import {
  fetchUserManagementUsers,
  fetchUserManagementUser,
  fetchUserManagementRoles,
  updateUserManagementUser,
  inviteUserManagementUser,
  addUserRole,
  removeUserRole,
} from "@shared/api/userManagement";
import useListPageUrlState from "../../hooks/useListPageUrlState";
import { ListPageLayout, SortableTh, CtaButton } from "../../components/admin/ListPageLayout";

function areSetsEqual(left, right) {
  if (left.size !== right.size) return false;
  for (const value of left) {
    if (!right.has(value)) return false;
  }
  return true;
}

function displayName(user) {
  return (user.name ?? [user.firstname, user.lastname].filter(Boolean).join(" ").trim()) || "—";
}

function statusPill(user) {
  if (user.deleted_at) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-700">
        Deleted
      </span>
    );
  }
  if (user.is_locked) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
        Locked
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
      Active
    </span>
  );
}

const MAX_VISIBLE_ROLES = 3;

function RolePills({ roles }) {
  if (!roles || !roles.length) {
    return <span className="text-slate-400">—</span>;
  }
  const visible = roles.slice(0, MAX_VISIBLE_ROLES);
  const extra = roles.length - MAX_VISIBLE_ROLES;
  return (
    <div className="flex flex-wrap items-center gap-1.5 gap-y-1">
      {visible.map((r) => (
        <span
          key={r.id}
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700"
        >
          {r.name}
        </span>
      ))}
      {extra > 0 && (
        <span className="text-xs text-slate-500" title={roles.slice(MAX_VISIBLE_ROLES).map((r) => r.name).join(", ")}>
          +{extra}
        </span>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function EditIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}

function LockIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );
}

function EditUserModal({ user, allRoles, customerSlug, onClose, onSaved }) {
  const [form, setForm] = useState({
    email: "",
    login: "",
    firstname: "",
    lastname: "",
  });
  const [selectedRoleIds, setSelectedRoleIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const computedName = [form.firstname, form.lastname].filter(Boolean).join(" ").trim() || "—";

  useEffect(() => {
    if (!user) return;
    setForm({
      email: user.email ?? "",
      login: user.login ?? "",
      firstname: user.firstname ?? "",
      lastname: user.lastname ?? "",
    });
    setSelectedRoleIds(new Set((user.roles || []).map((r) => String(r.id))));
  }, [user]);

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const toggleRole = useCallback((roleId) => {
    setSelectedRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!user || !customerSlug) return;
      setSaving(true);
      setError(null);
      try {
        await updateUserManagementUser(customerSlug, user.id, {
          email: form.email || null,
          login: form.login || null,
          firstname: form.firstname || null,
          lastname: form.lastname || null,
        });
        const previousIds = new Set((user.roles || []).map((r) => String(r.id)));
        for (const role of allRoles) {
          const rid = String(role.id);
          if (selectedRoleIds.has(rid) && !previousIds.has(rid)) {
            await addUserRole(customerSlug, user.id, role.id);
          } else if (!selectedRoleIds.has(rid) && previousIds.has(rid)) {
            await removeUserRole(customerSlug, user.id, role.id);
          }
        }
        onSaved({ type: "success", message: "User updated." });
        onClose();
      } catch (err) {
        setError(err.message || "Failed to save");
        /* Keep errors in the modal only; avoid duplicating ListPageLayout top flash. */
      } finally {
        setSaving(false);
      }
    },
    [user, customerSlug, form, selectedRoleIds, allRoles, onSaved, onClose]
  );

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [onClose]);

  if (!user) return null;

  const originalRoleIds = new Set((user.roles || []).map((r) => String(r.id)));
  const rolesChanged = !areSetsEqual(originalRoleIds, selectedRoleIds);
  const isDirty =
    rolesChanged ||
    form.email !== (user.email ?? "") ||
    form.login !== (user.login ?? "") ||
    form.firstname !== (user.firstname ?? "") ||
    form.lastname !== (user.lastname ?? "");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-user-title"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200">
          <h2 id="edit-user-title" className="text-lg font-semibold text-slate-800">
            Edit user
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 animate-notice-slide-in" role="alert">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Name
            </label>
            <div
              id="edit-name"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-700"
              aria-readonly
            >
              {computedName}
            </div>
          </div>
          <div>
            <label htmlFor="edit-firstname" className="block text-sm font-medium text-slate-700 mb-1">
              First name
            </label>
            <input
              id="edit-firstname"
              type="text"
              value={form.firstname}
              onChange={(e) => handleChange("firstname", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div>
            <label htmlFor="edit-lastname" className="block text-sm font-medium text-slate-700 mb-1">
              Last name
            </label>
            <input
              id="edit-lastname"
              type="text"
              value={form.lastname}
              onChange={(e) => handleChange("lastname", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div>
            <label htmlFor="edit-email" className="block text-sm font-medium text-slate-700 mb-1">
              Email
            </label>
            <input
              id="edit-email"
              type="email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div>
            <label htmlFor="edit-login" className="block text-sm font-medium text-slate-700 mb-1">
              Login
            </label>
            <input
              id="edit-login"
              type="text"
              value={form.login}
              onChange={(e) => handleChange("login", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div>
            <span className="block text-sm font-medium text-slate-700 mb-2">Roles</span>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-h-48 overflow-y-auto pr-1">
              {(allRoles || []).map((role) => (
                <label key={role.id} className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedRoleIds.has(String(role.id))}
                    onChange={() => toggleRole(String(role.id))}
                    className="rounded border-slate-300 text-slate-600 focus:ring-slate-500 shrink-0"
                  />
                  <span className="text-sm text-slate-700 truncate">{role.name}</span>
                </label>
              ))}
              {(!allRoles || allRoles.length === 0) && (
                <span className="col-span-2 text-sm text-slate-500">No roles available</span>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !isDirty}
              className="px-4 py-2 text-sm font-medium text-white bg-slate-700 rounded-lg hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function inviteErrorMessage(err) {
  const code = err.code || "";
  if (code === "USER_ALREADY_ACTIVE") return "This user is already active.";
  if (code === "USER_DISABLED") return "This user is disabled.";
  if (code === "email is required" || code === "VALIDATION_ERROR") return "Email is required.";
  if (code === "CUSTOMER_OR_POOL_NOT_FOUND") return "Customer or sign-in configuration not found.";
  if (code === "INVITE_EMAIL_SEND_FAILED") return "Invite could not be sent. Please try again.";
  return err.message || "Failed to send invite.";
}

function AddUserModal({ customerSlug, onClose, onSaved }) {
  const [form, setForm] = useState({
    email: "",
    firstname: "",
    lastname: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const emailTrimmed = form.email.trim();
  const canSubmit = Boolean(emailTrimmed);

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!customerSlug || !canSubmit) return;
      setSaving(true);
      setError(null);
      try {
        const res = await inviteUserManagementUser(customerSlug, {
          email: emailTrimmed,
          firstname: form.firstname.trim() || undefined,
          lastname: form.lastname.trim() || undefined,
        });
        const message = res.status === "reinvited" ? "Invite re-sent." : "Invite sent.";
        onSaved({ type: "success", message, fromInvite: true });
        onClose();
      } catch (err) {
        const msg = inviteErrorMessage(err);
        setError(msg);
        /* Inline error only; do not call onSaved — parent notice would duplicate the modal banner. */
      } finally {
        setSaving(false);
      }
    },
    [customerSlug, form.firstname, form.lastname, emailTrimmed, canSubmit, onSaved, onClose]
  );

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-user-title"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200">
          <h2 id="add-user-title" className="text-lg font-semibold text-slate-800">
            Add User
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 animate-notice-slide-in" role="alert">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="add-user-email" className="block text-sm font-medium text-slate-700 mb-1">
              Email <span className="text-red-600">*</span>
            </label>
            <input
              id="add-user-email"
              type="email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="add-user-firstname" className="block text-sm font-medium text-slate-700 mb-1">
                First name
              </label>
              <input
                id="add-user-firstname"
                type="text"
                value={form.firstname}
                onChange={(e) => handleChange("firstname", e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
            <div>
              <label htmlFor="add-user-lastname" className="block text-sm font-medium text-slate-700 mb-1">
                Last name
              </label>
              <input
                id="add-user-lastname"
                type="text"
                value={form.lastname}
                onChange={(e) => handleChange("lastname", e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              Cancel
            </button>
            <CtaButton type="submit" disabled={saving || !canSubmit}>
              {saving ? "Sending…" : "Send invite"}
            </CtaButton>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminUsersPage({ customerSlug }) {
  const { fetchParams, query, setQuery, page, setPage, perPage, setPerPage, sort, order, setSort } = useListPageUrlState();
  const [result, setResult] = useState({ data: [], meta: { total_count: 0, total_pages: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [allRoles, setAllRoles] = useState([]);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [notice, setNotice] = useState(null);

  const loadList = useCallback(() => {
    if (!customerSlug) return Promise.resolve();
    setLoading(true);
    setError(null);
    return fetchUserManagementUsers(customerSlug, fetchParams)
      .then(setResult)
      .catch((err) => setError(err.message || "Failed to load users"))
      .finally(() => setLoading(false));
  }, [customerSlug, fetchParams]);

  useEffect(() => {
    if (!customerSlug) return;
    loadList();
  }, [customerSlug, loadList]);

  const openEdit = useCallback(
    async (user) => {
      setEditUser(user);
      setEditModalLoading(true);
      try {
        const [fullUser, rolesRes] = await Promise.all([
          fetchUserManagementUser(customerSlug, user.id),
          fetchUserManagementRoles(customerSlug, {}),
        ]);
        setEditUser(fullUser);
        setAllRoles(rolesRes.data || []);
      } catch (err) {
        setError(err.message || "Failed to load user");
        setEditUser(null);
      } finally {
        setEditModalLoading(false);
      }
    },
    [customerSlug]
  );

  const handleDeactivate = useCallback(
    async (user) => {
      if (user.is_locked) return;
      if (!window.confirm(`Lock user "${displayName(user)}"? They will not be able to sign in.`)) return;
      try {
        await updateUserManagementUser(customerSlug, user.id, { is_locked: true });
        setNotice({ type: "success", message: "User deactivated." });
        loadList();
      } catch (err) {
        setError(err.message || "Failed to lock user");
        setNotice({ type: "error", message: err.message || "Failed to lock user" });
      }
    },
    [customerSlug, loadList]
  );

  const totalCount = result.meta?.total_count ?? 0;
  const totalPages = result.meta?.total_pages ?? 1;
  const pageData = result.data;

  return (
    <>
      <ListPageLayout
        title=""
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="Search by name, email, or login…"
        actions={<CtaButton onClick={() => setShowAddUser(true)}>Add User</CtaButton>}
        notice={notice}
        onNoticeDismiss={() => setNotice(null)}
        loading={loading}
        error={error}
        totalCount={totalCount}
        page={page}
        totalPages={totalPages}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
      >
        <table className="min-w-full divide-y divide-slate-200" role="grid">
          <thead className="bg-slate-100 border-b border-slate-200">
            <tr>
              <SortableTh
                label="Login"
                sortKey="login"
                currentSort={sort}
                currentOrder={order}
                onSort={setSort}
                className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
              />
              <SortableTh
                label="Name"
                sortKey="firstname"
                currentSort={sort}
                currentOrder={order}
                onSort={setSort}
                className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
              />
              <SortableTh
                label="Email"
                sortKey="email"
                currentSort={sort}
                currentOrder={order}
                onSort={setSort}
                className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
              />
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                Roles
              </th>
              <SortableTh
                label="Last login"
                sortKey="last_sign_in_at"
                currentSort={sort}
                currentOrder={order}
                onSort={setSort}
                className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
              />
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  No users found.
                </td>
              </tr>
            ) : (
              pageData.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-slate-700">{user.login ?? "—"}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-slate-900">{displayName(user)}</div>
                    <div className="mt-1">{statusPill(user)}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{user.email ?? "—"}</td>
                  <td className="px-4 py-3">
                    <RolePills roles={user.roles} />
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{formatDate(user.last_sign_in_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(user)}
                        className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                        title="Edit"
                        aria-label="Edit user"
                      >
                        <EditIcon className="w-4 h-4" />
                      </button>
                      {!user.deleted_at && (
                        <button
                          type="button"
                          onClick={() => handleDeactivate(user)}
                          disabled={!!user.is_locked}
                          className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={user.is_locked ? "Already locked" : "Deactivate (lock)"}
                          aria-label={user.is_locked ? "Already locked" : "Deactivate user"}
                        >
                          <LockIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </ListPageLayout>

      {showAddUser && (
        <AddUserModal
          customerSlug={customerSlug}
          onClose={() => setShowAddUser(false)}
          onSaved={(result) => {
            if (result?.message) setNotice({ type: result.type || "success", message: result.message });
            if (result?.fromInvite && result?.type === "success") {
              loadList().catch(() => {});
            } else {
              loadList();
            }
          }}
        />
      )}
      {editUser && (
        editModalLoading ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50" aria-busy="true">
            <div className="bg-white rounded-xl shadow-xl px-8 py-6 text-slate-600">Loading…</div>
          </div>
        ) : (
          <EditUserModal
            user={editUser}
            allRoles={allRoles}
            customerSlug={customerSlug}
            onClose={() => setEditUser(null)}
            onSaved={(result) => {
              if (result?.message) setNotice({ type: result.type || "success", message: result.message });
              loadList();
            }}
          />
        )
      )}
    </>
  );
}
